import { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldOff, UserMinus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adoptionConfirmationCopy, type AdoptRoleResult, type RoleAdoptionPreview } from '@/lib/roleAdoption';
import { RoleAdoptionError, adoptRole, fetchRoleAdoptionPreview } from '@/services/roleAdoptionService';

/**
 * RBAC-3.4 · FR-2a. The confirm step of Adopt: states how many people move, what they lose, and who
 * stays on the Joules role and why, before anything happens.
 *
 * Sends back the count and dropped list it showed. `adopt_role()` refuses (22023) if either changed,
 * and this dialog then re-reads and shows the new numbers rather than moving anyone on a stale count.
 */
export interface AdoptRoleDialogProps {
  open: boolean;
  sourceRoleId: number | null;
  onOpenChange: (open: boolean) => void;
  onAdopted: (result: AdoptRoleResult) => void;
}

export function AdoptRoleDialog({ open, sourceRoleId, onOpenChange, onAdopted }: AdoptRoleDialogProps) {
  const [preview, setPreview] = useState<RoleAdoptionPreview | null>(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (id: number, keepName: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const p = await fetchRoleAdoptionPreview(id);
      setPreview(p);
      if (!keepName) setName(p.suggestedName);
    } catch (e) {
      setPreview(null);
      setError(e instanceof Error ? e.message : 'The role could not be read.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || sourceRoleId == null) return;
    setPreview(null);
    setNotice(null);
    void load(sourceRoleId, false);
  }, [open, sourceRoleId, load]);

  const confirm = async () => {
    if (!preview) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await adoptRole(preview, name, null);
      onAdopted(result);
      onOpenChange(false);
    } catch (e) {
      if (e instanceof RoleAdoptionError && e.code === '22023') {
        // Who moves, or what drops, changed since this opened. Show the new numbers; never move on the old ones.
        setNotice('Who would move has changed since this opened. Review it again before adopting.');
        await load(preview.sourceRoleId, true);
      } else {
        setError(e instanceof Error ? e.message : 'The role could not be adopted.');
      }
    } finally {
      setSaving(false);
    }
  };

  const copy = preview ? adoptionConfirmationCopy(preview) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{copy?.title ?? 'Adopt role'}</DialogTitle>
          {copy && <DialogDescription>{copy.summary}</DialogDescription>}
        </DialogHeader>

        {loading && !preview && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Counting who would move…
          </div>
        )}

        {copy && (
          <div className="space-y-4">
            {copy.losingIntro && (
              <div className="space-y-2">
                <p className="text-sm text-foreground">{copy.losingIntro}</p>
                <ul className="space-y-2 rounded-md border border-border bg-muted/40 p-3" aria-label="Permissions the people who move lose">
                  {copy.losing.map((line) => (
                    <li key={line.key} className="flex gap-2 text-sm">
                      <ShieldOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span>
                        <span className="font-medium">{line.label}</span>
                        {line.description && <span className="block text-muted-foreground">{line.description}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {copy.staying.length > 0 && (
              <ul className="space-y-1.5" aria-label="Who stays on the Joules role">
                {copy.staying.map((line) => (
                  <li key={line} className="flex gap-2 text-sm text-muted-foreground">
                    <UserMinus className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            )}

            {copy.canAdopt && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="adopt-role-name">Name of your copy</Label>
                  <Input
                    id="adopt-role-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={preview?.suggestedName}
                    disabled={saving}
                  />
                </div>
                <p className="text-sm text-muted-foreground">{copy.note}</p>
              </>
            )}
          </div>
        )}

        {notice && <p className="text-sm text-amber-600 dark:text-amber-400">{notice}</p>}
        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {copy && !copy.canAdopt ? 'Close' : 'Cancel'}
          </Button>
          {(!copy || copy.canAdopt) && (
            <Button onClick={confirm} disabled={!preview || loading || saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {copy?.confirmLabel ?? 'Adopt'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AdoptRoleDialog;
